/**
 * Pure helper functions for workspace app data mapping and SSE event handling.
 * Extracted to be independently testable without DOM/React dependencies.
 */

import type { AppData } from "./AppContainer";
import type { SSEEvent } from "../hooks/useSSE";

// ─── Backend → AppData mapping ────────────────────────────────────────────────

export interface BackendAppResponse {
  id: string;
  title?: string;
  html?: string;
  designSystem?: boolean;
  sourceMode?: "render_spec" | "legacy_html";
  templateId?: string;
  compilerVersion?: string;
}

/**
 * Maps a raw backend app response to the client `AppData` type, applying
 * defaults for fields added in Task A.5 that older/minimal backends may omit.
 */
export function mapBackendAppToAppData(data: BackendAppResponse, fallbackTitle?: string): AppData {
  return {
    id: data.id,
    title: data.title || fallbackTitle || data.id,
    html: data.html ?? "",
    designSystem: data.designSystem ?? true,
    sourceMode: data.sourceMode ?? "legacy_html",
    templateId: data.templateId,
    compilerVersion: data.compilerVersion,
  };
}

// ─── Inline artifact open routing ─────────────────────────────────────────────

export interface InlineArtifactOpenInput {
  artifactId: string;
  title?: string;
  kind?: string;
}

export type InlineArtifactOpenTarget =
  | { kind: "workspace_app"; appId: string; title?: string }
  | { kind: "artifact"; artifactId: string };

export function resolveInlineArtifactOpenTarget(
  artifact: InlineArtifactOpenInput,
): InlineArtifactOpenTarget {
  if (artifact.kind === "workspace_app") {
    return { kind: "workspace_app", appId: artifact.artifactId, title: artifact.title };
  }

  return { kind: "artifact", artifactId: artifact.artifactId };
}

// ─── SSE app-event handler ────────────────────────────────────────────────────

const APP_SSE_EVENTS = new Set([
  "workspace:app_created",
  "workspace:app_updated",
  "workspace:app_removed",
]);

export interface HandleAppSSEEventsParams {
  events: SSEEvent[];
  processedKeys: Set<string>;
  /** Called with the app id when app_created or app_updated fires */
  refetchApp: (appId: string) => void;
  /** Called with the app id when app_removed fires */
  closeApp: (appId: string) => void;
}

/**
 * Processes SSE events for workspace app lifecycle changes.
 *
 * Payload key contract (preserved as-is from server emitter):
 * - workspace:app_created  → event.data.app.id  (nested object)
 * - workspace:app_updated  → event.data.app.id  (nested object)
 * - workspace:app_removed  → event.data.appId   (flat key)
 */
export function handleAppSSEEvents({
  events,
  processedKeys,
  refetchApp,
  closeApp,
}: HandleAppSSEEventsParams): void {
  for (const event of events) {
    if (!APP_SSE_EVENTS.has(event.event)) continue;

    const eventKey = `${event.event}-${event.timestamp}`;
    if (processedKeys.has(eventKey)) continue;
    processedKeys.add(eventKey);

    const data = event.data as Record<string, unknown>;

    if (event.event === "workspace:app_removed") {
      // Server emits: broadcastEvent("workspace:app_removed", { appId: req.params.id })
      const appId = data.appId as string | undefined;
      if (appId) closeApp(appId);
    } else {
      // workspace:app_created and workspace:app_updated both use nested app object:
      // broadcastEvent("workspace:app_updated", { app: updated })
      const appObj = data.app as { id?: string } | undefined;
      const appId = appObj?.id;
      if (appId) refetchApp(appId);
    }
  }
}
